import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Loader2, AlertCircle, RefreshCw, Menu, X, LogOut, ExternalLink,
  LifeBuoy, FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { to: "/partner/dashboard",   label: "Dashboard",     testId: "dashboard" },
  { to: "/partner/customers",   label: "Customers",     testId: "customers" },
  { to: "/partner/commissions", label: "Commissions",   testId: "commissions" },
  { to: "/partner/codes",       label: "Codes",         testId: "codes" },
  { to: "/partner/payouts",     label: "Payouts",       testId: "payouts" },
  { to: "/partner/marketing",   label: "Marketing kit", testId: "marketing" },
];

const _initials = (name, email) => {
  const src = (name || email || "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

function PartnerTopNav() {
  const { user, workspace, memberships, logout } = useAuth() || {};
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const hasWorkspace = !!(workspace || (memberships && memberships.length > 0));
  const doLogout = async () => {
    try { await logout?.(); } catch { /* ignore */ }
    nav("/login", { replace: true });
  };
  const linkCls = ({ isActive }) =>
    `px-1 py-2 text-sm transition-colors ${
      isActive
        ? "text-[#D6A653] border-b-2 border-[#D6A653]"
        : "text-white/60 border-b-2 border-transparent hover:text-white"
    }`;

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/8 bg-[#050607]/95 backdrop-blur"
      data-testid="partner-topnav"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
        {/* Brand */}
        <Link to="/partner/dashboard" className="flex items-center gap-2 shrink-0" data-testid="partner-topnav-brand">
          <img src="/tp-mark.png" alt="TapPresence" className="h-7 w-7 object-contain" />
          <div className="hidden sm:block leading-tight">
            <div className="text-[13px] font-semibold text-white">TapPresence</div>
            <div className="text-[10px] uppercase tracking-wider text-[#D6A653]/80">Partner Portal</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-5 ml-2">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              className={linkCls}
              data-testid={`partner-topnav-nav-${n.testId}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-3">
          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden inline-flex items-center justify-center rounded-lg border border-white/10 p-2 text-white/70 hover:text-white"
            aria-label="Open menu"
            data-testid="partner-topnav-mobile-toggle"
          >
            <Menu className="h-4 w-4" />
          </button>

          {/* User avatar dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="partner-topnav-user"
                className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-2 py-1.5 text-xs text-white/85 hover:bg-white/[0.06]"
              >
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#D6A653] text-[10px] font-semibold text-black">
                  {_initials(user?.name, user?.email)}
                </span>
                <span className="hidden sm:inline max-w-[140px] truncate">{user?.name || user?.email || "Account"}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 border-white/10 bg-[#0B0D12] text-white">
              <DropdownMenuLabel className="pb-1">
                <div className="text-[13px] font-medium text-white">{user?.name || "Partner"}</div>
                <div className="text-[11px] font-normal text-white/45 truncate">{user?.email || ""}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/8" />
              {hasWorkspace && (
                <DropdownMenuItem asChild>
                  <Link
                    to="/dashboard"
                    className="cursor-pointer text-white/85 focus:bg-white/[0.06] focus:text-white"
                    data-testid="partner-topnav-back-to-app"
                  >
                    <ExternalLink className="mr-2 h-3.5 w-3.5" /> Back to TapPresence
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <a href="mailto:partners@tappresence.com" className="cursor-pointer text-white/85 focus:bg-white/[0.06] focus:text-white" data-testid="partner-topnav-support">
                  <LifeBuoy className="mr-2 h-3.5 w-3.5" /> Support
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/legal/partner-terms" className="cursor-pointer text-white/85 focus:bg-white/[0.06] focus:text-white" data-testid="partner-topnav-terms">
                  <FileText className="mr-2 h-3.5 w-3.5" /> Partner Terms
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/8" />
              <DropdownMenuItem
                onClick={doLogout}
                className="cursor-pointer text-red-300 focus:bg-red-500/10 focus:text-red-200"
                data-testid="partner-topnav-logout"
              >
                <LogOut className="mr-2 h-3.5 w-3.5" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-[#050607]/95 backdrop-blur md:hidden" data-testid="partner-topnav-mobile-drawer">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
            <div className="flex items-center gap-2">
              <img src="/tp-mark.png" alt="TapPresence" className="h-7 w-7 object-contain" />
              <span className="text-[13px] font-semibold text-white">Partner Portal</span>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg border border-white/10 p-2 text-white/70"
              aria-label="Close menu"
              data-testid="partner-topnav-mobile-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <nav className="flex flex-col p-4">
            {NAV_ITEMS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-3 text-sm transition-colors ${
                    isActive ? "bg-white/[0.06] text-[#D6A653]" : "text-white/80 hover:bg-white/[0.04]"
                  }`
                }
                data-testid={`partner-topnav-nav-mobile-${n.testId}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

function PartnerFooter() {
  return (
    <footer
      className="border-t border-white/8 bg-[#050607]"
      data-testid="partner-footer"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-2 px-6 py-4 text-[12px] text-white/40 sm:flex-row sm:items-center">
        <p>© {new Date().getFullYear()} TapPresence</p>
        <div className="flex flex-wrap items-center gap-4">
          <a href="mailto:partners@tappresence.com" className="hover:text-white/70" data-testid="partner-footer-support">Support</a>
          <Link to="/legal/partner-terms" className="hover:text-white/70" data-testid="partner-footer-terms">Terms</Link>
          <Link to="/privacy" className="hover:text-white/70" data-testid="partner-footer-privacy">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}

/**
 * The single source of partner-portal chrome. Every partner route wraps its
 * body in this so the user always sees the persistent top nav + footer,
 * regardless of loading / empty / error / active status.
 */
export const PartnerShell = ({ children, testId }) => (
  <div
    className="min-h-screen w-full flex flex-col bg-[#050607] text-white"
    data-testid={testId}
    style={{ fontFamily: "'Outfit', sans-serif" }}
  >
    <PartnerTopNav />
    <main className="flex-1">
      <div className="mx-auto max-w-6xl px-6 py-8">{children}</div>
    </main>
    <PartnerFooter />
  </div>
);

// Convenience alias — matches spec language.
export const PartnerAppShell = PartnerShell;

export const PartnerLoading = ({ testId, label = "Loading…" }) => (
  <div
    className="mt-8 flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/8 bg-white/[0.02] py-16 text-white/55"
    data-testid={testId}
  >
    <Loader2 className="h-6 w-6 animate-spin text-[#D6A653]" />
    <p className="text-sm">{label}</p>
  </div>
);

export const PartnerEmpty = ({ testId, title, sub, action = null }) => (
  <div
    className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center"
    data-testid={testId}
  >
    <p className="text-sm font-medium text-white/85">{title}</p>
    {sub ? <p className="mt-2 text-xs text-white/50">{sub}</p> : null}
    {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
  </div>
);

export const PartnerError = ({ testId, message = "Something went wrong.", onRetry }) => {
  const loc = useLocation();
  return (
    <div
      className="mt-8 rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-6 text-center"
      data-testid={testId}
    >
      <AlertCircle className="mx-auto h-6 w-6 text-red-300" />
      <p className="mt-2 text-sm text-red-200">{message}</p>
      <p className="mt-1 text-[11px] text-white/40">Route: {loc.pathname}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`${testId}-retry`}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Retry
        </button>
      ) : null}
    </div>
  );
};
